import { ElementType } from "react"
import styled, { PolymorphicComponentProps } from "styled-components"
import { ErrorIcon } from "../../icons"
import { Typography } from "../../typography"
import { DeprecatedSpinner } from "../spinner"
import { AttachmentProps, DeprecatedAttachment } from "./attachment"
import { DeprecatedDropzone, DropzoneProps } from "./dropzone"

const Container = styled.div``

const FieldError = styled(Typography).attrs(() => ({
  as: "strong",
  $variant: "caption-2",
}))`
  color: ${({ theme }) => theme.colors.danger.$200};
  display: inline-block;
  overflow: hidden;
  margin-top: 0.1rem;
  margin-bottom: 0.4rem;
  min-height: 1.125rem;
`

export type UploadFileFieldProps<
  FileType extends { name: string; size?: string },
  C extends ElementType,
  D extends ElementType,
  F extends ElementType,
  G extends ElementType,
  E extends ElementType,
> = PolymorphicComponentProps<
  "web",
  {
    errorMessage?: string
    dropzoneProps?: DropzoneProps<D>
    attachmentProps?: Partial<AttachmentProps<E, F, G>>
    value: FileType[]
    onFilesChange: (changes: { add: File[] } | { delete: FileType }) => void
    loading?: boolean
    disabled?: boolean
  },
  C,
  C
>

const DropzoneOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.75);
`

export const DeprecatedUploadFileField = <
  FileType extends { name: string; size?: string },
  C extends ElementType = "div",
  D extends ElementType = "div",
  E extends ElementType = "div",
  F extends ElementType = "button",
  G extends ElementType = "button",
>({
  dropzoneProps,
  errorMessage,
  attachmentProps = {},
  value,
  loading,
  onFilesChange,
  children,
  disabled,
  ...containerProps
}: UploadFileFieldProps<FileType, C, D, E, F, G>) => {
  return (
    <Container {...containerProps}>
      <div
        style={{
          position: "relative",
        }}
      >
        <DeprecatedDropzone
          // {...dropzoneProps}
          inputProps={dropzoneProps.inputProps}
          dropzoneOption={{
            ...dropzoneProps.dropzoneOption,
            onDrop: (files: File[]) => {
              if (dropzoneProps.dropzoneOption?.onDrop) {
                dropzoneProps.dropzoneOption?.onDrop(files)
              }
              onFilesChange({ add: files })
            },
          }}
        >
          {children}
        </DeprecatedDropzone>

        {loading && !disabled && (
          <DropzoneOverlay>
            <DeprecatedSpinner />
          </DropzoneOverlay>
        )}

        {disabled && (
          <DropzoneOverlay>
            <ErrorIcon />
          </DropzoneOverlay>
        )}
      </div>
      <FieldError>{errorMessage}</FieldError>
      {value.map((file: FileType, i: number) => {
        return (
          <DeprecatedAttachment
            key={i + file.name}
            // {...attachmentProps}
            name={file.name}
            size={file.size}
            onDelete={() => {
              onFilesChange({ delete: file })
            }}
          />
        )
      })}
    </Container>
  )
}
